import { CollapsibleDiv, Input, Pill, Typography } from '@recruit-robin/robin-theme/web-components';
import { LayersThree_01 } from '@recruit-robin/robin-theme/web-icons';
import { useContext, useEffect, useState } from 'react';
import { Controller, get, useFormContext } from 'react-hook-form';
import Range from 'shared/legacy/components/Range';
import { SingleLocationSelect } from 'shared/legacy/components/SingleLocationSelect/SingleLocationSelect';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { getWeekdays, shiftOptions, workdayOptions } from '../../../../utils/EnrichVacancyModal.constants';
import { isLocationResolved } from '../../../../utils/EnrichVacancyModal.mappers';
import { JobFormValues } from '../../EditEnrichedVacancy.types';
import { CustomSelectForm } from './CustomSelectForm';
import { useStyles } from './FormSections.styles';
import { ControllerRenderArgs, RenderField } from './RenderField';
import { useQualityStatus } from './useQualityStatus';

export const EmploymentRequirements = () => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);
  const { errors, control, setValue, getValues, watch } = useFormContext<JobFormValues>();

  const latitude = (watch('employment_requirements.latitude') as number | null | undefined) ?? null;
  const longitude = (watch('employment_requirements.longitude') as number | null | undefined) ?? null;

  const fieldConfig = {
    address: {
      required: true,
      weight: 30,
      isFilled: (value: string) => isLocationResolved({ address: value, latitude, longitude }),
    },
    radius_km: { weight: 30 },
    min_working_hours: { weight: 15 },
    max_working_hours: { weight: 15 },
    shifts: { weight: 5 },
    workdays: { weight: 5 },
  };

  const { statusConfig } = useQualityStatus<JobFormValues>('employment_requirements', fieldConfig);

  const onChangeAddress = ({
    address,
    city,
    latitude,
    longitude,
    place_id,
  }: {
    address: string;
    city: string;
    latitude: number | null;
    longitude: number | null;
    place_id: string | null;
  }) => {
    setValue('employment_requirements.city', city, { shouldDirty: true });
    setValue('employment_requirements.latitude', latitude ?? undefined, { shouldDirty: true });
    setValue('employment_requirements.longitude', longitude ?? undefined, { shouldDirty: true });
    setValue('employment_requirements.place_id', place_id ?? undefined, { shouldDirty: true });
    setValue('employment_requirements.address', address, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <CollapsibleDiv
      initialOpen
      limitToggleToTitle
      key="employment_requirements"
      title={
        <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
          {dictionary.employmentRequirements}
        </Typography>
      }
      endAdornment={
        <Pill
          startIcon={statusConfig.icon}
          label={statusConfig.label}
          style="icon-removable"
          type={statusConfig.type}
          padding="text"
        />
      }
      customCss={classes.section}>
      <div css={classes.sectionContent}>
        <Controller name="employment_requirements.city" control={control} defaultValue="" render={() => <></>} />
        <Controller
          name="employment_requirements.latitude"
          control={control}
          defaultValue={null}
          render={() => <></>}
        />
        <Controller
          name="employment_requirements.longitude"
          control={control}
          defaultValue={null}
          render={() => <></>}
        />
        <Controller
          name="employment_requirements.place_id"
          control={control}
          defaultValue={null}
          render={() => <></>}
        />
        <div css={classes.formRow}>
          <RenderField
            required
            label={dictionary.workAddress}
            fieldName="employment_requirements.address"
            validate={(value) =>
              isLocationResolved({
                address: value,
                latitude: getValues('employment_requirements.latitude'),
                longitude: getValues('employment_requirements.longitude'),
              })
                ? true
                : dictionary.pleaseSelectAValidLocation
            }
            render={({ value, onBlur }) => (
              <SingleLocationSelect
                addressValue={value}
                onAddressChange={onChangeAddress}
                onBlur={onBlur}
                placeholder={dictionary.workAddressPlaceholder}
                label=""
                error={get(errors, 'employment_requirements.address')?.message}
              />
            )}
          />

          <RenderField
            label={dictionary.radiusKm}
            fieldName="employment_requirements.radius_km"
            render={RangeFieldRender}
          />
        </div>

        <div css={classes.twoColumnGrid}>
          <div css={classes.formRow}>
            <RenderField
              label={dictionary.minWorkingHours}
              fieldName="employment_requirements.min_working_hours"
              render={(controllerArg) => (
                <Input
                  {...controllerArg}
                  type="text"
                  inputMode="numeric"
                  pattern="\\d*"
                  placeholder={dictionary.minWorkingHoursPlaceholder}
                  contained
                  hasError={get(errors, 'employment_requirements.min_working_hours')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d]/g, '');
                    controllerArg.onChange(value);
                  }}
                />
              )}
            />
            <RenderField
              label={dictionary.maxWorkingHours}
              fieldName="employment_requirements.max_working_hours"
              render={(controllerArg) => (
                <Input
                  {...controllerArg}
                  type="text"
                  inputMode="numeric"
                  pattern="\\d*"
                  placeholder={dictionary.maxWorkingHoursPlaceholder}
                  contained
                  hasError={get(errors, 'employment_requirements.max_working_hours')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d]/g, '');
                    controllerArg.onChange(value);
                  }}
                />
              )}
            />
          </div>
          <div css={classes.formRow}>
            <RenderField
              label={dictionary.shifts}
              fieldName="employment_requirements.shifts"
              render={() => (
                <CustomSelectForm
                  control={control}
                  fieldName="employment_requirements.shifts"
                  options={shiftOptions}
                  label={dictionary.shifts}
                  placeholder={dictionary.shiftPlaceholder}
                  multiple
                  limit
                />
              )}
            />

            <RenderField
              label={dictionary.workdays}
              fieldName="employment_requirements.workdays"
              key="workdays"
              render={() => (
                <CustomSelectForm
                  control={control}
                  fieldName="employment_requirements.workdays"
                  options={workdayOptions}
                  label={dictionary.workdays}
                  placeholder={dictionary.workdaysPlaceholder}
                  multiple
                  limit
                  selectPartial={{
                    label: dictionary.allWorkDays,
                    icon: LayersThree_01,
                    values: getWeekdays(),
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>
    </CollapsibleDiv>
  );
};

const MARKED_DISTANCES = [1, 20, 40, 60, 80, 100];
const DISTANCES = [1, ...Array.from({ length: 20 }, (_, i) => (i + 1) * 5)];
const MARKS = DISTANCES.map((distance, i) => ({
  label: MARKED_DISTANCES.includes(distance) ? distance.toString() : '',
  value: i,
  distance,
}));

const RangeFieldRender = ({ value, onChange }: ControllerRenderArgs) => {
  const [sliderValue, setSliderValue] = useState<number>(4);

  useEffect(() => {
    const index = DISTANCES.findIndex((distance) => distance === value);

    setSliderValue(index === -1 ? 4 : index);
  }, [value]);

  const handleChangeCommitted = (value: number) => {
    onChange(DISTANCES[value]);
  };

  return (
    <Range
      onChange={(_, v) => setSliderValue(v as number)}
      value={sliderValue}
      onChangeCommitted={(_, v) => handleChangeCommitted(v as number)}
      marks={MARKS}
      min={0}
      max={DISTANCES.length - 1}
      step={1}
      valueLabelFormat={(value) => `${DISTANCES[value]}`}
      sliderPadding="0 10px"
      contained
    />
  );
};
